(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const SECTION_SELECTOR = '[data-ls-milestone]';
  const MOBILE_BREAKPOINT = 767;
  const START_SCROLL_WEIGHT = 0.9;
  const LOOP_SCROLL_WEIGHT = 0.5;
  const END_SCROLL_WEIGHT = 0.8;
  const LABEL_FADE_OUT_END = 0.42;
  const LABEL_FADE_IN_START = 0.58;
  const DEFAULT_MILESTONES = [
    { title: 'Kickoff', desc: 'The journey begins', percentage: 0 },
    { title: 'Planning', desc: 'Roadmap creation', percentage: 30 },
    { title: 'Design', desc: 'Wireframes & mockups', percentage: 60 },
    { title: 'Launch', desc: 'Product goes live', percentage: 100 }
  ];

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const interpolate = (start, end, progress) => start + (end - start) * progress;

  const progressBetween = (value, start, end) => {
    if (end === start) return value >= end ? 1 : 0;
    return clamp((value - start) / (end - start));
  };

  const createSvgElement = (tagName, attributes = {}) => {
    const element = document.createElementNS(SVG_NS, tagName);

    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== null && value !== undefined) element.setAttribute(key, value);
    });

    return element;
  };

  const getViewportHeight = () => window.visualViewport?.height || window.innerHeight;
  const isMobileViewport = () => window.innerWidth <= MOBILE_BREAKPOINT;

  const getSectionData = (section) => {
    const script = section.querySelector('[data-milestone-data]');

    if (script) {
      try {
        const parsed = JSON.parse(script.textContent);

        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch (error) {
        console.warn('Milestone data could not be parsed.', error);
      }
    }

    return DEFAULT_MILESTONES;
  };

  const normalizeMilestones = (items) => items
    .map((item) => ({
      title: item.title || '',
      desc: item.desc || '',
      buttonText: item.button_text || '',
      buttonLink: item.button_link || '',
      fraction: clamp((Number(item.percentage) || 0) / 100),
      horizontalAlign: item.label_horizontal_align || 'right',
      verticalAlign: item.label_vertical_align || 'bottom'
    }))
    .sort((a, b) => a.fraction - b.fraction);

  const createMilestoneNode = ({ item, point, group }) => {
    const itemGroup = createSvgElement('g', {
      class: 'ls-milestone__item',
      transform: `translate(${point.x} ${point.y})`
    });
    const dot = createSvgElement('circle', {
      class: 'ls-milestone__dot',
      cx: 0,
      cy: 0
    });
    const label = createSvgElement('g', {
      class: `ls-milestone__label ls-milestone__label--horizontal-${item.horizontalAlign} ls-milestone__label--vertical-${item.verticalAlign}`
    });
    const foreignObject = createSvgElement('foreignObject', {
      class: 'ls-milestone__label-frame',
      x: 0,
      y: 0,
      width: 1,
      height: 1
    });
    const content = document.createElement('div');
    const title = document.createElement('div');
    const description = document.createElement('div');

    content.className = 'ls-milestone__label-content';
    title.className = 'ls-milestone__label-title';
    title.textContent = item.title;
    description.className = 'ls-milestone__label-description';
    description.innerHTML = item.desc;

    if (item.buttonText && item.buttonLink) {
      const button = document.createElement('a');

      button.className = 'ls-milestone__button';
      button.href = item.buttonLink;
      button.textContent = item.buttonText;
      description.appendChild(button);
    }

    content.append(title, description);
    foreignObject.appendChild(content);
    label.append(foreignObject);
    itemGroup.append(dot, label);
    group.append(itemGroup);

    return {
      fraction: item.fraction,
      cy: point.y,
      dot,
      label,
      isActive: false,
      isVisible: false
    };
  };

  const initSection = (section) => {
    if (section.dataset.lsMilestoneInitialized === 'true') return;

    const svg = section.querySelector('[data-ls-milestone-svg]');
    const progressPath = section.querySelector('[data-ls-milestone-path-progress]');
    const itemsGroup = section.querySelector('[data-ls-milestone-items]');
    const intro = section.querySelector('[data-ls-milestone-intro]');

    if (!svg || !progressPath || !itemsGroup) return;

    section.dataset.lsMilestoneInitialized = 'true';

    const pathLength = progressPath.getTotalLength();
    const viewBox = svg.viewBox.baseVal;
    const sourceData = normalizeMilestones(getSectionData(section));
    const milestoneData = sourceData.length ? sourceData : normalizeMilestones(DEFAULT_MILESTONES);
    let mobileLayout = isMobileViewport();
    let milestones = [];
    let anchors = [];
    let frameId = null;

    progressPath.style.strokeDasharray = '1';
    progressPath.style.strokeDashoffset = '1';

    const buildMilestones = () => {
      itemsGroup.textContent = '';
      milestones = milestoneData.map((item) => {
        const point = progressPath.getPointAtLength(pathLength * item.fraction);

        return createMilestoneNode({
          item,
          point,
          group: itemsGroup
        });
      });
    };

    const calculateAnchors = () => {
      const svgRect = svg.getBoundingClientRect();
      const svgTop = svgRect.top + window.scrollY;
      const scaleY = svgRect.height / viewBox.height;
      const revealLine = getViewportHeight() * (mobileLayout ? 0.55 : 0.52);
      const minGap = Math.max(48, getViewportHeight() * 0.08);
      const points = milestones.map((milestone) => ({
        fraction: milestone.fraction,
        y: milestone.cy
      }));

      if (!points.length || points[0].fraction > 0) {
        points.unshift({ fraction: 0, y: progressPath.getPointAtLength(0).y });
      }

      if (points[points.length - 1].fraction < 1) {
        points.push({ fraction: 1, y: progressPath.getPointAtLength(pathLength).y });
      }

      const nextAnchors = [];
      let hasLoopedUpward = false;

      points.forEach((point, index) => {
        const naturalScroll = svgTop + (point.y - viewBox.y) * scaleY - revealLine;
        const previous = nextAnchors[nextAnchors.length - 1];
        let scroll = naturalScroll;

        if (previous && point.fraction > previous.fraction) {
          const fractionSpan = point.fraction - previous.fraction;
          const loopsUpward = point.y <= points[index - 1].y;
          const naturalGap = Math.max(minGap, (point.y - points[index - 1].y) * scaleY);

          if (loopsUpward) {
            // Allocate scroll distance to portions of the path that turn upward.
            scroll = previous.scroll + Math.max(minGap, svgRect.height * fractionSpan * LOOP_SCROLL_WEIGHT);
            hasLoopedUpward = true;
          } else if (hasLoopedUpward) {
            scroll = previous.scroll + naturalGap * END_SCROLL_WEIGHT;
          } else {
            scroll = previous.scroll + naturalGap * START_SCROLL_WEIGHT;
          }
        }

        nextAnchors.push({ fraction: point.fraction, scroll });
      });

      anchors = nextAnchors;
    };

    const resolvePathProgress = (scrollPosition) => {
      if (!anchors.length) return 0;
      if (scrollPosition <= anchors[0].scroll) return anchors[0].fraction;

      for (let index = 0; index < anchors.length - 1; index += 1) {
        const current = anchors[index];
        const next = anchors[index + 1];

        if (scrollPosition <= next.scroll) {
          return interpolate(
            current.fraction,
            next.fraction,
            progressBetween(scrollPosition, current.scroll, next.scroll)
          );
        }
      }

      return anchors[anchors.length - 1].fraction;
    };

    const getMilestoneWeights = (pathProgress) => {
      if (!milestones.length) return [];
      if (milestones.length === 1) return [1];

      const weights = milestones.map(() => 0);

      if (pathProgress <= milestones[0].fraction) {
        weights[0] = 1;
        return weights;
      }

      if (pathProgress >= milestones[milestones.length - 1].fraction) {
        weights[milestones.length - 1] = 1;
        return weights;
      }

      for (let index = 0; index < milestones.length - 1; index += 1) {
        const current = milestones[index];
        const next = milestones[index + 1];

        if (pathProgress >= current.fraction && pathProgress <= next.fraction) {
          const progress = progressBetween(pathProgress, current.fraction, next.fraction);

          weights[index] = 1 - progress;
          weights[index + 1] = progress;
          break;
        }
      }

      return weights;
    };

    const getLabelOpacities = (pathProgress) => {
      if (!milestones.length) return [];
      if (milestones.length === 1) return [1];

      const opacities = milestones.map(() => 0);

      if (pathProgress <= milestones[0].fraction) {
        opacities[0] = 1;
        return opacities;
      }

      if (pathProgress >= milestones[milestones.length - 1].fraction) {
        opacities[milestones.length - 1] = 1;
        return opacities;
      }

      for (let index = 0; index < milestones.length - 1; index += 1) {
        const current = milestones[index];
        const next = milestones[index + 1];

        if (pathProgress >= current.fraction && pathProgress <= next.fraction) {
          const progress = progressBetween(pathProgress, current.fraction, next.fraction);

          opacities[index] = 1 - progressBetween(progress, 0, LABEL_FADE_OUT_END);
          opacities[index + 1] = progressBetween(progress, LABEL_FADE_IN_START, 1);
          break;
        }
      }

      return opacities;
    };

    const render = () => {
      frameId = null;

      const pathProgress = clamp(resolvePathProgress(window.scrollY));
      const weights = getMilestoneWeights(pathProgress);
      const labelOpacities = getLabelOpacities(pathProgress);

      progressPath.style.strokeDashoffset = `${1 - pathProgress}`;

      if (intro) {
        intro.style.opacity = clamp(labelOpacities[0] * 1.15);
      }

      milestones.forEach((milestone, index) => {
        const opacity = clamp(labelOpacities[index] * 1.15);
        const isVisible = opacity > 0.02;
        const isActive = weights[index] > 0.5;

        milestone.label.style.setProperty('--ls-milestone-label-alpha', opacity);
        milestone.label.style.visibility = isVisible ? 'visible' : 'hidden';
        milestone.label.style.pointerEvents = opacity > 0.6 ? 'auto' : 'none';

        if (milestone.isVisible !== isVisible) {
          milestone.label.classList.toggle('is-visible', isVisible);
          milestone.isVisible = isVisible;
        }

        if (milestone.isActive !== isActive) {
          milestone.dot.classList.toggle('is-active', isActive);
          milestone.label.classList.toggle('is-active', isActive);
          milestone.isActive = isActive;
        }
      });
    };

    const requestRender = () => {
      if (frameId === null) frameId = requestAnimationFrame(render);
    };

    const refresh = () => {
      const nextMobileLayout = isMobileViewport();

      mobileLayout = nextMobileLayout;
      calculateAnchors();
      requestRender();
    };

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(refresh)
      : null;

    buildMilestones();
    calculateAnchors();
    render();

    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    window.visualViewport?.addEventListener('resize', refresh, { passive: true });
    resizeObserver?.observe(svg);

    section.addEventListener('shopify:section:unload', () => {
      window.removeEventListener('scroll', requestRender);
      window.removeEventListener('resize', refresh);
      window.visualViewport?.removeEventListener('resize', refresh);
      resizeObserver?.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
      section.dataset.lsMilestoneInitialized = 'false';
    }, { once: true });
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
